"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useForm } from "react-hook-form";

import { trpc } from "~/app/providers";
import LoadingModal from "~/components/LoadingModal";

type Inputs = {
  name: string;
  email: string;
  businessName: string;
  businessAddress: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
};
export default function Page() {
  const { register, handleSubmit } = useForm<Inputs>();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const trialRequest = trpc.admin.trialRequest.useMutation({
    onSuccess: () => {
      setFormSubmitted(true);
      setLoading(false);
    },
  });

  return (
    <>
      {formSubmitted ? (
        <div className="mt-12 min-h-screen">
          <main className="mx-auto w-full max-w-7xl px-6 pb-16 pt-10 sm:pb-24 lg:px-8">
            <Image
              width={1000}
              height={1000}
              quality={100}
              alt="Your Company"
              src="/images/logo_transparent.png"
              className="mx-auto h-10 w-auto sm:h-12"
            />
            <div className="mx-auto mt-20 max-w-2xl text-center sm:mt-24">
              <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Your response has been received
              </h1>
              <p className="mt-4 text-base leading-7 text-gray-600 sm:mt-6 sm:text-lg sm:leading-8">
                Keep an eye on your email! Biblish will be in touch.
              </p>
            </div>
            <div className="mx-auto mt-16 flow-root max-w-lg sm:mt-20">
              <h2 className="sr-only">Popular pages</h2>

              <div className="mt-10 flex justify-center">
                <Link
                  href="/"
                  className="text-sm font-semibold leading-6 text-teal-600"
                >
                  <span aria-hidden="true">&larr;</span>
                  Back to home
                </Link>
              </div>
            </div>
          </main>
        </div>
      ) : (
        <div className="min-h-screen">
          <div className="mt-16 flex flex-col">
            {/* column wrapper */}
            <div className="mx-auto grid w-full max-w-7xl grow grid-cols-1 lg:grid-cols-2 xl:px-2">
              <div className="px-4 py-6 text-center sm:px-6 lg:pl-8 xl:pl-6">
                <form
                  onSubmit={handleSubmit((data) => {
                    setLoading(true);
                    trialRequest.mutate(data);
                  })}
                >
                  <div className="">
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      Your Information
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Complete this form to get started with Biblish
                    </p>

                    <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          Your Name
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("name", { required: true })}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          Email
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("email", { required: true })}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-full">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          Business Name
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("businessName", { required: true })}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="col-span-full">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          Street address
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("businessAddress", { required: true })}
                            type="text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2 sm:col-start-1">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          City
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("businessCity", { required: true })}
                            type="text"
                            autoComplete="address-level2"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          State
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("businessState", { required: true })}
                            type="text"
                            autoComplete="address-level1"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium leading-6 text-gray-900">
                          ZIP / Postal code
                        </label>
                        <div className="mt-2">
                          <input
                            {...register("businessZip", { required: true })}
                            autoComplete="postal-code"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-12 rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                  >
                    Request Trial
                  </button>
                </form>
              </div>

              <div className="border-t border-gray-200 px-4 py-6 text-center sm:px-6 lg:border-l lg:border-t-0 lg:pr-8 xl:pr-6">
                <p className="h-full rounded-xl bg-white p-4">
                  Thank you for your interest in The Biblish Library. With a
                  subscription to The Biblish Library, your shop will receive a
                  merchandising display and books, configured with a QR code
                  that allows customers to purchase the books. Biblish will mail
                  the order to the customer, and your shop will receive 33% of
                  the sale price.
                  <br />
                  <br />
                  If you are interested in the dynamic this program might bring
                  to your coffee shop, but you feel unable to commit to a
                  subscription, we here at Biblish are happy to offer you a free
                  12-month trial of this program.
                  <br />
                  <br />
                  Biblish is on a mission to improve the process of reading,
                  writing, publishing, and distributing literature, at scale,
                  and in a durable manner, in order to positively contributes to
                  the American literary landscape in the long-term. We are happy
                  to offer your shop this free trial, because we believe that
                  partnering with small, independent coffee shops to create a
                  national distribution network is a great way to realize this
                  ambition.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? <LoadingModal text="Just a sec..." /> : null}
    </>
  );
}
